import { reportErrorMessages } from '@repo/lib/messages/reportErrorMessages';
import { useMutation } from '@tanstack/react-query';

import { useErrorMessage } from '#error-message/useErrorMessage';

import { ReportService, type StudentReportData, type StudentReportResult } from './reportService';

export interface GenerateStudentReportVariables {
  studentId: number;
  data: StudentReportData;
}

/**
 * Generates a student's PDF report and returns the bytes plus the filename and
 * parent-email count the API reported.
 *
 * Saving the result is left to the caller: this package is compiled by
 * apps/native too, so it stays free of DOM APIs.
 *
 * Nothing here is cached or invalidated — the endpoint is a POST that renders a
 * document and (optionally) sends mail, so it owns no query state.
 */
export function useGenerateStudentReport() {
  const generateStudentReportMutation = useMutation<StudentReportResult, Error, GenerateStudentReportVariables>({
    mutationFn: ({ studentId, data }) => ReportService.generateStudentReport(studentId, data),
  });

  const errorMessage = useErrorMessage(generateStudentReportMutation.error, reportErrorMessages);

  return { ...generateStudentReportMutation, errorMessage };
}
